<template>
  <div class="border rounded-lg">
    <template v-if="rules.length">
      <div
        :class="['grid gap-4 px-4 p-3', shouldShowFormation ? 'md:grid-cols-5' : 'md:grid-cols-4']"
      >
        <div><p class="text-sm font-bold text-black">Dias da semana</p></div>
        <div><p class="text-sm font-bold text-black">Turno</p></div>
        <div><p class="text-sm font-bold text-black">Posições</p></div>
        <div><p class="text-sm font-bold text-black">Time</p></div>
        <div v-if="shouldShowFormation">
          <p class="text-sm font-bold text-black">Formação</p>
        </div>
      </div>

      <div v-for="(rule, index) in rules" :key="index" class="mb-2 px-4 border-t">
        <div
          :class="['grid gap-4 my-3', shouldShowFormation ? 'md:grid-cols-5' : 'md:grid-cols-4']"
        >
          <DsMultipleSelect
            v-model="rule.days"
            :items="days"
            :error="!!errors?.[`rules[${index}].days`]"
            :error-message="errors?.[`rules[${index}].days`]"
          />
          <DsMultipleSelect
            v-model="rule.shifts"
            :items="shifts"
            :error="!!errors?.[`rules[${index}].shifts`]"
            :error-message="errors?.[`rules[${index}].shifts`]"
          />
          <DsMultipleSelect
            v-model="rule.positions"
            :items="positions"
            :error="!!errors?.[`rules[${index}].positions`]"
            :error-message="errors?.[`rules[${index}].positions`]"
          />
          <DsMultipleSelect
            v-model="rule.teams"
            :items="teams"
            :error="!!errors?.[`rules[${index}].teams`]"
            :error-message="errors?.[`rules[${index}].teams`]"
          />
          <DsMultipleSelect
            v-if="shouldShowFormation"
            v-model="rule.formations"
            :items="formations"
          />
        </div>

        <div class="w-full flex flex-wrap justify-end items-center gap-4 border-t py-4">
          <div class="flex items-center gap-2 min-w-[220px]">
            <label class="text-sm font-bold text-gray-900 whitespace-nowrap"
              >Modalidade de repasse:</label
            >
            <v-autocomplete
              v-model="rule.repassMode"
              :items="repassModes"
              item-title="label"
              item-value="value"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              class="w-full min-w-[128px]"
              color="primary"
              :error="!!errors?.[`rules[${index}].repassMode`]"
              :error-messages="errors?.[`rules[${index}].repassMode`]"
            />
          </div>

          <div v-if="showAntecipeRule" class="flex items-center gap-2 min-w-[260px]">
            <label class="text-sm font-bold text-gray-900 whitespace-nowrap"
              >Regra para antecipação:</label
            >
            <v-autocomplete
              :model-value="rule.antecipeRule ?? ANTECIPE_RULE_DEFAULT"
              :items="antecipeRuleOptions"
              item-title="title"
              item-value="value"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              class="w-full min-w-[220px]"
              color="primary"
              :data-testid="`payment-rule-antecipe-rule-${index}`"
              @update:model-value="rule.antecipeRule = $event"
            />
          </div>

          <div class="flex items-center gap-2">
            <label class="text-sm font-bold text-gray-900 whitespace-nowrap">Valor por hora:</label>
            <v-text-field
              v-model="rule.dutyValue"
              @input="rule.dutyValue = $event.target.value.replace(/\,/g, '')"
              placeholder="Valor/hora"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              class="w-full min-w-[140px]"
              color="primary"
              type="number"
              :error="!!errors?.[`rules[${index}].dutyValue`]"
              :error-messages="errors?.[`rules[${index}].dutyValue`]"
            >
              <template #prepend-inner>
                <span class="text-primary font-bold mr-1">R$</span>
              </template>
            </v-text-field>
          </div>

          <v-tooltip text="Duplicar regra">
            <template #activator="{ props }">
              <v-icon
                v-bind="props"
                :icon="mdiContentCopy"
                size="20"
                class="cursor-pointer text-primary hover:text-primary5"
                @click="copyRule(index)"
              />
            </template>
          </v-tooltip>

          <v-tooltip text="Excluir regra">
            <template #activator="{ props }">
              <v-icon
                v-if="rules.length > 1"
                v-bind="props"
                :icon="mdiTrashCan"
                size="20"
                class="cursor-pointer text-red-500 hover:text-red-700"
                @click="confirmDelete(index)"
              />
            </template>
          </v-tooltip>
        </div>
        <p
          v-if="showAntecipeRule"
          class="m-0 pb-3 text-right text-xs text-neutral50"
          :data-testid="`payment-rule-antecipe-rule-help-${index}`"
        >
          Vale só para plantão antecipado, a partir da data de corte. Padrão = Proporcional com
          limite; exceção aprovada continua valendo acima desta regra.
        </p>
      </div>
    </template>
    <EmptyList v-else title="Nenhuma política de pagamento encontrada" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import DsMultipleSelect from '@/components/designSystem/Select/DsMultipleSelect.vue'
import EmptyList from '@/components/EmptyList.vue'
import { mdiTrashCan, mdiContentCopy } from '@mdi/js'
import { Item, ItemTeams, PaymentRule } from '@/pinia-store/useCreateScheduleStore'
import { ANTECIPE_RULE_DEFAULT, antecipeRuleOptions } from './antecipeRule'

const props = defineProps<{
  rules: PaymentRule[]
  shifts: Item[]
  positions: Item[]
  teams: ItemTeams[]
  days: Item[]
  formations: Item[]
  formationRequired?: boolean
  repassModes: { label: string; value: string }[]
  confirmDelete: (index: number) => void
  copyRule: (index: number) => void
  errors?: Record<string, string>
  /** Frente A / Q8 — só o modal da política de pagamento da escala liga o campo. */
  showAntecipeRule?: boolean
}>()

const shouldShowFormation = computed(() => props.formationRequired === true)
</script>
